import { useState, useEffect, useContext } from "react";
import ListAndAddTabs from "components/Layout/ListAndAddTabs";
import Page from "components/Page";
import { GET_POI_TYPES } from "lib/pois";
import { useQuery } from "@apollo/client";
import {
  GetFloorOverlaysAndroidQueryResult,
  GetPoiTypesQueryResult,
  FloorOverlay,
  PoiType,
} from "generated/graphql";
import {
  Checkbox,
  Grid,
  Typography,
  Card,
  LinearProgress,
} from "@material-ui/core";
import styled from "styled-components";
import PoiMap from "Modules/BuildingConfig/PointsOfInterest/PoiMap";
import { GET_FLOOR_OVERLAYS_ANDROID } from "lib/buildings";
import { BuildingContext } from "Context/BuildingProvider";
import { StandardContainer } from "components/Layout/StandardStyledComponents";
import AddEditPoi from "Modules/BuildingConfig/PointsOfInterest/AddEditPoi";
import { AuthContext } from "Firebase/AuthProvider";

const CardContainer = styled.div`
  padding: 12px;
`;
const PoiTypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export interface PoiLocation {
  lat: number;
  lng: number;
  floorNumber: number;
}
export interface PoiInput {
  name: string;
  type?: string;
  location?: PoiLocation;
}
interface PointsOfInterestState {
  selectedPoiIds: string[];
  currentFloorNumber: number;
  editing: boolean;
  tabValue: number;
  currentPoi: PoiInput;
}

const DEFAULT_STATE: PointsOfInterestState = {
  currentFloorNumber: 0,
  selectedPoiIds: [],
  editing: false,
  tabValue: 0,
  currentPoi: {
    name: "",
  },
};

export default function PointsOfInterest() {
  const [state, setState] = useState(DEFAULT_STATE);
  const { currentBuilding, updateBuilding } = useContext(BuildingContext);
  const { setRoute } = useContext(AuthContext);
  useEffect(() => {
    setRoute("Points of Interest");
  }, []);
  const { loading, error, data }: GetPoiTypesQueryResult =
    useQuery(GET_POI_TYPES);
  const floorOverlaysQueryResult: GetFloorOverlaysAndroidQueryResult = useQuery(
    GET_FLOOR_OVERLAYS_ANDROID,
    {
      variables: {
        buildingId: currentBuilding?.Id,
      },
    }
  );
  const onOptionClick = (id: string) => {
    if (state.selectedPoiIds.includes(id)) {
      const newSelectedPOIs = state.selectedPoiIds.filter((rts) => {
        return rts !== id;
      });
      setState({ ...state, selectedPoiIds: newSelectedPOIs });
    } else {
      const newSelectedPOIs = state.selectedPoiIds;
      newSelectedPOIs.push(id);
      setState({ ...state, selectedPoiIds: newSelectedPOIs });
    }
  };
  useEffect(() => {
    if (data) {
      let selectedPoiIds: string[] = [];
      data.PoiType.forEach((poiType) => {
        selectedPoiIds.push(poiType.id);
      });

      setState({ ...state, selectedPoiIds });
    }
  }, [data]);

  const handleTabChange = (newValue: number) => {
    setState({ ...state, tabValue: newValue });
  };

  const handlePoiTypeChange = () => {};

  const handleNewLocation = (
    lat: number,
    lng: number,
    floorNumber: number
  ) => {};

  if (loading || floorOverlaysQueryResult.loading) {
    return <LinearProgress />;
  } else if (error) {
    return <div>{error.message}</div>;
  } else if (floorOverlaysQueryResult.error) {
    return <div>{floorOverlaysQueryResult.error.message}</div>;
  } else if (!!floorOverlaysQueryResult.data?.FloorOverlay) {
    const list = (
      <StandardContainer>
        <Card>
          <CardContainer>
            <Grid container spacing={3}>
              {data?.PoiType.map((poiType) => {
                const checked = state.selectedPoiIds.includes(poiType.id);
                return (
                  <Grid key={poiType.id} item xs={3} sm={3}>
                    <PoiTypeContainer>
                      <Checkbox
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        onClick={() => {
                          onOptionClick(poiType.id);
                        }}
                        disableRipple
                      />
                      <Typography>{poiType.title}</Typography>
                    </PoiTypeContainer>
                  </Grid>
                );
              })}
            </Grid>
          </CardContainer>
        </Card>
        <PoiMap
          poiTypes={data?.PoiType as unknown as PoiType[]}
          poiTypeIds={state.selectedPoiIds}
          floorOverlays={
            floorOverlaysQueryResult.data.FloorOverlay as unknown as Pick<
              FloorOverlay,
              | "BuildingId"
              | "CenterLat"
              | "CenterLng"
              | "D_diff"
              | "FloorNumber"
              | "Id"
              | "ImageUrl"
              | "Name"
              | "Rot"
              | "created_at"
              | "k_diff"
              | "updated_at"
              | "AndroidFloors"
            >[]
          }
        />
      </StandardContainer>
    );

    const add = (
      <AddEditPoi
        floorOverlays={
          floorOverlaysQueryResult.data.FloorOverlay as unknown as Pick<
            FloorOverlay,
            | "BuildingId"
            | "CenterLat"
            | "CenterLng"
            | "D_diff"
            | "FloorNumber"
            | "Id"
            | "ImageUrl"
            | "Name"
            | "Rot"
            | "created_at"
            | "k_diff"
            | "updated_at"
            | "AndroidFloors"
          >[]
        }
        handlePoiTypeChange={handlePoiTypeChange}
        handleNewLocation={handleNewLocation}
        poiTypes={data?.PoiType as unknown as PoiType[]}
        type={state.currentPoi.type}
      />
    );
    return (
      <Page title="Points of Interest">
        <ListAndAddTabs
          value={state.tabValue}
          handleChange={handleTabChange}
          listChildren={list}
          addChildren={add}
          fromEdit={state.editing}
        />
      </Page>
    );
  } else {
    return <div>error</div>;
  }
}
